import type { SyntheticEvent } from 'react';
import { Toggletip, ToggletipButton, ToggletipContent } from '@carbon/react';
import { Information } from '@carbon/react/icons';
import type {
  DataTableColumnDef,
  DataTableColumnMeta,
} from 'components/data-table';
import { blanksLast, formatCellValue } from 'components/data-table/utils';
import { FormattedCount } from 'components/formatted-count';
import { LaunchLink } from 'components/launch-link';
import { EMPTY } from 'components/vesting-documents/types';
import styles from './target-npv.module.scss';

// The header sorts on click; the info button opens its note without sorting.
const keepHeaderClick = (event: SyntheticEvent) => event.stopPropagation();

export const TARGET_NPV_SOURCE = 'From the newest package evaluation';
const SOURCE_NOTE = `${TARGET_NPV_SOURCE}; click to open it.`;

export const TargetNpvHeader = ({ note = SOURCE_NOTE }: { note?: string }) => (
  <span className={styles.header}>
    Target NPV
    <span onClick={keepHeaderClick} onKeyDown={keepHeaderClick} role="none">
      <Toggletip align="bottom-start" autoAlign>
        <ToggletipButton label="Where Target NPV comes from">
          <Information />
        </ToggletipButton>
        <ToggletipContent>
          <p className={styles.note}>{note}</p>
        </ToggletipContent>
      </Toggletip>
    </span>
  </span>
);

export const TARGET_NPV_META: DataTableColumnMeta = {
  type: 'currency',
  format: { minDecimalPlaces: 0, maxDecimalPlaces: 0 },
};

export const TargetNpvCell = ({
  value,
  href,
}: {
  value: number | null;
  href?: string | null;
}) => {
  if (value === null || value === undefined) return <>{EMPTY}</>;
  const text = formatCellValue(value, TARGET_NPV_META);
  if (!href) return <>{text}</>;
  return (
    <LaunchLink to={href} label={`Open the package behind ${text}`}>
      {text}
    </LaunchLink>
  );
};

// A sum over the tracts that have a value, flagged when some have none.
export const TargetNpvSum = ({
  value,
  valued,
  total,
}: {
  value: number | null;
  valued: number;
  total: number;
}) => (
  <>
    <TargetNpvCell value={value} />
    {value !== null && valued < total && (
      <div className={styles.valued}>
        <FormattedCount value={valued} /> of <FormattedCount value={total} />{' '}
        tracts valued
      </div>
    )}
  </>
);

const SUM_NOTE =
  'The newest package evaluation of each tract, summed over the tracts that have one.';

// As TargetNpvSum shows it: a partial sum says how many tracts it covers.
const targetNpvSumText = (
  value: number | null,
  valued: number,
  total: number
) => {
  const text = formatCellValue(value, TARGET_NPV_META);
  return value !== null && valued < total
    ? `${text} (${valued} of ${total} tracts valued)`
    : text;
};

// A Target NPV summed over a row's tracts; `sum` reads [value, valued, total] off the row.
export const targetNpvSumColumn = <Row,>(
  sum: (row: Row) => [number | null, number, number],
  meta: DataTableColumnMeta = {}
) => ({
  header: () => <TargetNpvHeader note={SUM_NOTE} />,
  meta: {
    ...meta,
    label: 'Target NPV',
    getExportValue: <TData,>(row: TData) =>
      targetNpvSumText(...sum(row as unknown as Row)),
  },
  cell: ({ row }: { row: { original: Row } }) => {
    const [value, valued, total] = sum(row.original);
    return <TargetNpvSum value={value} valued={valued} total={total} />;
  },
});

export const targetNpvColumn = <
  Row extends { targetNpvIncluded?: number | null },
>(
  getHref: (row: Row) => string | null | undefined
): DataTableColumnDef<Row> => ({
  ...blanksLast<Row>('targetNpvIncluded'),
  header: () => <TargetNpvHeader />,
  meta: { ...TARGET_NPV_META, label: 'Target NPV' },
  cell: ({ row }) => (
    <TargetNpvCell
      value={row.original.targetNpvIncluded ?? null}
      href={getHref(row.original)}
    />
  ),
});

// Inserted after Instrument Type, or last without it.
export const withTargetNpvColumn = <
  Row extends {
    targetNpvIncluded: number | null;
    targetNpvHref: string | null;
  },
>(
  columns: DataTableColumnDef<Row>[]
): DataTableColumnDef<Row>[] => {
  const targetNpv = {
    ...targetNpvColumn<Row>((row) => row.targetNpvHref),
    size: 220,
  };
  const at =
    columns.findIndex((column) => column.id === 'instrumentType') + 1 ||
    columns.length;
  return [...columns.slice(0, at), targetNpv, ...columns.slice(at)];
};
